/**
 * Export Piano Turni in template XLSX importabile.
 */

import { getState } from './store.js';
import { getWeekStart, getDaysInWeekRange } from './utils.js';
import { apiExportShiftPlanXlsx } from './api.js';
import { getAbsenceCalendarCellLabel } from './absenceCellLabel.js';
import { getOfficialSlotsForDates } from './shiftGenerator.js';
import { ensurePlannerSlotName } from './utils.js';

function compactText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function isDashLike(text) {
  const t = compactText(text);
  return t === '-' || t === '—' || t === '·' || t === '+';
}

function weekdayFromDate(dateStr) {
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    const wd = d.getDay();
    return ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'][wd] || '';
  } catch (_) {
    return '';
  }
}

function plannerTimeTextToSlot(timeTextRaw) {
  const t = compactText(timeTextRaw).toUpperCase();
  if (t === 'M') return 'mattino';
  if (t === 'P') return 'pomeriggio';
  if (t === 'PR') return 'prolungato';
  if (t === 'N') return 'notte';
  if (t === 'RG') return 'reperibilita_giorno';
  if (t === 'RN' || t === 'R') return 'reperibilita_notte';
  return '';
}

function defaultActivityType(category, timeSlot) {
  if ((timeSlot || '').startsWith('reperibilita')) return 'reperibilita';
  if ((category || '') === 'direzione') return 'direzione';
  if ((category || '') === 'formazione') return 'non_clinico';
  return 'clinico';
}

function pickCategoryFromCell(td) {
  const classes = Array.from(td?.classList || []);
  const matches = classes
    .filter((c) => c.startsWith('cat-bg-'))
    .map((c) => c.replace(/^cat-bg-/, ''))
    .filter((x) => !['rep-slot', 'ti-notte'].includes(x));
  const raw = matches[0] || '';
  const map = {
    ti: 'terapia_intensiva',
    so: 'sala_operatoria',
    nora: 'nora',
    md: 'medicina_dolore',
    cp: 'cure_palliative',
    formazione: 'formazione',
    dir: 'direzione',
  };
  return map[raw] || raw || '';
}

function extractUocFromSlotKey(slotKey) {
  const key = String(slotKey || '').trim();
  if (!key) return '';
  const parts = key.split('-');
  if (parts.length < 7) return '';
  return parts[5] || '';
}

function parseCellDoctorAndKey(td) {
  const badge = td.querySelector('.grid-sigla');
  const lockIcon = td.querySelector('.slot-lock-icon');
  const select = td.querySelector('select.slot-assign-select');
  const slotKey = String(
    select?.dataset?.slotKey
      || lockIcon?.dataset?.slotKey
      || badge?.dataset?.slotId
      || ''
  ).trim();
  const badgeSigla = compactText(badge?.textContent || '');
  if (badgeSigla) return { doctorSigla: badgeSigla, slotKey };
  if (select) {
    const opt = select.options?.[select.selectedIndex];
    const txt = compactText(opt?.textContent || '');
    if (!txt || isDashLike(txt) || txt.startsWith('—')) return { doctorSigla: '', slotKey };
    return { doctorSigla: txt.split(' ')[0] || txt, slotKey };
  }
  return { doctorSigla: '', slotKey };
}

function _slotWeekStart(dateStr) {
  const d = String(dateStr || '').trim();
  if (!d) return '';
  return String(getWeekStart(d) || '').trim();
}

function _itDateToken(dateStr) {
  const s = String(dateStr || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s.replace(/[^\d-]/g, '');
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function collectRowsFromStateForWeeks(weekStarts, state) {
  const wantedWeeks = new Set((weekStarts || []).map((w) => String(w || '').trim()).filter(Boolean));
  const allDates = [...new Set(
    [...wantedWeeks].flatMap((ws) => getDaysInWeekRange(ws, ws).map((d) => String(d?.date || '').trim()).filter(Boolean))
  )];
  // Slot "ufficiali" del Piano Turni: stessa logica/merge usata in UI.
  // Evita doppie righe quando lo stesso slot logico esiste sia da calendario sia da DB
  // con slot_key diversi (storico/import) o nomi non normalizzati.
  const slots = getOfficialSlotsForDates(allDates);
  const doctorsById = new Map(
    (state?.doctors || [])
      .filter((d) => d?.id)
      .map((d) => [String(d.id), String(d.sigla || '').trim()])
  );
  const byKey = state?.shiftAssignmentsByKey || {};
  const rows = [];
  slots.forEach((slot) => {
    if (!slot) return;
    const date = String(slot.date || '').trim();
    if (!date) return;
    const weekStart = _slotWeekStart(date);
    if (wantedWeeks.size > 0 && !wantedWeeks.has(weekStart)) return;
    const category = String(slot.category || '').trim();
    const timeSlot = String(slot.timeSlot || '').trim();
    if (!category || !timeSlot) return;
    const slotKey = String(
      slot.slotKey
      || `${date}-${timeSlot}-${category}-${String(slot.uocId || '').trim()}-tpl`
    ).trim();
    const doctorId = byKey[slotKey];
    const doctorSigla = doctorId ? String(doctorsById.get(String(doctorId)) || '').trim() : '';
    const activityType = String(slot.activityType || defaultActivityType(category, timeSlot) || '').trim();
    const slotName = ensurePlannerSlotName(category, slot.name, timeSlot, activityType);
    rows.push({
      slot_key: slotKey,
      week_start: weekStart,
      date,
      weekday: String(slot.weekday || weekdayFromDate(date) || '').trim(),
      time_slot: timeSlot,
      category,
      uoc_id: String(slot.uocId || '').trim(),
      slot_name: slotName,
      activity_type: activityType,
      doctor_sigla: doctorSigla,
    });
  });
  return rows;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportShiftPlanToExcel({ root = document, filename = null } = {}) {
  const state = getState();
  const isUserRole = (state?.currentUser?.role || '') === 'user';
  const weekBlocks = Array.from(root.querySelectorAll('.week-block')).filter((block) => {
    if (!isUserRole) return true;
    const ws = String(block.dataset.week || '').trim();
    return ws ? true : false;
  });
  if (weekBlocks.length === 0) {
    return { ok: false, error: 'Nessuna settimana disponibile da esportare.' };
  }
  const weekStartsFromUi = weekBlocks
    .map((block) => String(block?.dataset?.week || '').trim())
    .filter(Boolean);
  const rows = collectRowsFromStateForWeeks(weekStartsFromUi, state);
  if (rows.length === 0) {
    return { ok: false, error: 'Nessun dato turno disponibile per export XLSX.' };
  }
  const weekStarts = [...new Set(rows.map((r) => r.week_start).filter(Boolean))].sort();
  const doctors = Array.from(state?.doctors || [])
    .filter((d) => d && d.id && String(d.sigla || '').trim())
    .sort((a, b) => {
      const ac = String(a.cognome || '');
      const bc = String(b.cognome || '');
      return ac.localeCompare(bc, 'it', { sensitivity: 'base' });
    })
    .map((d) => ({
      id: d.id,
      sigla: String(d.sigla || '').trim(),
      cognome: String(d.cognome || '').trim(),
      nome: String(d.nome || '').trim(),
    }));
  const datesByWeek = new Map();
  rows.forEach((r) => {
    const ws = String(r.week_start || '').trim();
    const dt = String(r.date || '').trim();
    if (!ws || !dt) return;
    if (!datesByWeek.has(ws)) datesByWeek.set(ws, new Set());
    datesByWeek.get(ws).add(dt);
  });
  const absenceRows = [];
  for (const ws of weekStarts) {
    const dates = [...(datesByWeek.get(ws) || new Set())].sort();
    if (dates.length === 0) continue;
    const rangeStart = dates[0];
    const rangeEnd = dates[dates.length - 1];
    for (const doc of doctors) {
      for (const dt of dates) {
        const lbl = getAbsenceCalendarCellLabel(doc.id, dt, rangeStart, rangeEnd) || '';
        absenceRows.push({
          week_start: ws,
          date: dt,
          doctor_sigla: doc.sigla,
          label: String(lbl || ''),
        });
      }
    }
  }
  const firstWeekIso = String(getWeekStart(weekStarts[0] || '') || weekStarts[0] || '').trim();
  const lastWeekIso = String(getWeekStart(weekStarts[weekStarts.length - 1] || '') || weekStarts[weekStarts.length - 1] || '').trim();
  const firstToken = _itDateToken(firstWeekIso);
  const lastToken = _itDateToken(lastWeekIso);
  const outName = filename || `Piano_Turni_${firstToken || 'from'}_${lastToken || 'to'}.xlsx`;
  try {
    const result = await apiExportShiftPlanXlsx(rows, {
      title: 'Piano turni',
      rangeLabel: `${weekStarts[0] || ''} - ${weekStarts[weekStarts.length - 1] || ''}`.trim(),
      filename: outName,
      doctors,
      absenceRows,
    });
    downloadBlob(result.blob, result.filename || outName);
    return { ok: true, filename: result.filename || outName };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}
